import { State } from '@progress/kendo-data-query';
import { FC, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { SUPPLIER_TYPE_FILTER_ITEMS } from 'src/constants/manageSuppliers';
import useDebounce from 'src/hooks/useDebounce';
import useRedirect from 'src/hooks/useRedirect';
import useGetCatalogsQuery from 'src/services/catalog_products/useGetCatalogsQuery';
import useGetMarketplaceCategories from 'src/services/marketplace/useGetMarketplaceCategories';
import useGetProgramInfo from 'src/services/marketplace/useGetProgramInfo';
import { buildOnlineSupplierFilterRequest } from 'src/services/manage_suppliers/buildSupplierFilterRequest';
import useSuppliersQuery from 'src/services/manage_suppliers/useSuppliersQuery';
import { Catalog } from 'src/types/catalog';
import { SetState } from 'src/types/generic';
import { ManageSupplier } from 'src/types/manage-suppliers/ManageSupplier';
import { MarketplaceCategoriesResponse } from 'src/types/view_catalogs/MarketplaceCategories';
import { SupplierCatalog } from 'src/types/view_catalogs/SupplierCatalog';
import { createContext } from 'use-context-selector';
import {
  buildBrowseCatalogsFilter,
  DEFAULT_BROWSE_CATALOGS_DATA_STATE,
  DEFAULT_SELECTED_CATEGORY,
  getCategoryOptionsForSuppliers,
  resolveSelectedCategory,
  SupplierOption,
} from './ViewCatalogsUtils';


export type { SupplierOption };

export interface ViewCatalogsContextShape {
  catalogs: Catalog[];
  catalogDataState: State;
  isLoadingCatalogs: boolean;
  isGridLoading: boolean;
  categoryOptions: MarketplaceCategoriesResponse[];
  selectedCategory: MarketplaceCategoriesResponse;
  setSelectedCategory: (category: MarketplaceCategoriesResponse | null) => void;
  selectedSupplier: SupplierOption[];
  setSelectedSupplier: (supplier: SupplierOption[]) => void;
  searchText: string;
  setSearchText: SetState<string>;
  dataState: State;
  setDataState: SetState<State>;
  hasActiveFilters: boolean;
  clearFilters: () => void;
  selectedCatalog: SupplierCatalog | null;
  supplierInfo: ManageSupplier | undefined;
  isLoadingSupplierInfo: boolean;
  isSupplierInfoPanelOpen: boolean;
  openSupplierInfo: (catalog: SupplierCatalog) => void;
  closeSupplierInfo: () => void;
  openProgramInfo: (catalogProgramId: string) => void;
}

export const ViewCatalogsContext = createContext<ViewCatalogsContextShape>(
  {} as ViewCatalogsContextShape
);



export const ViewCatalogsContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation();
  const { redirect } = useRedirect();

  const { data: marketplaceCategories = [], isError: isErrorMarketplaceCategories } =
    useGetMarketplaceCategories();
  const { catalogs = [], isLoading: isLoadingCatalogs, isError: isErrorCatalogs } =
    useGetCatalogsQuery();

  useEffect(() => {
    if (isErrorMarketplaceCategories || isErrorCatalogs) {
      toast.error(t('Failed to load data'));
    }
  }, [isErrorMarketplaceCategories, isErrorCatalogs, t]);

  const [selectedSupplier, setSelectedSupplier] = useState<SupplierOption[]>(
    SUPPLIER_TYPE_FILTER_ITEMS
  );
  const [selectedCategory, setSelectedCategory] = useState<MarketplaceCategoriesResponse | null>(
    DEFAULT_SELECTED_CATEGORY
  );
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText.trim(), 500);
  const [dataState, setDataState] = useState<State>(DEFAULT_BROWSE_CATALOGS_DATA_STATE);
  const [selectedCatalog, setSelectedCatalog] = useState<SupplierCatalog | null>(null);
  const [selectedProgramInfoId, setSelectedProgramInfoId] = useState<string | null>(null);

  const supplierFilterRequest = useMemo(
    () => buildOnlineSupplierFilterRequest(selectedCatalog?.supplierId ?? 0),
    [selectedCatalog?.supplierId]
  );
  const {
    data: supplierInfoData,
    isLoading: isLoadingSupplierInfo,
    isError: isErrorSupplierInfo,
  } = useSuppliersQuery({
    request: supplierFilterRequest,
    enabled: selectedCatalog?.supplierId != null,
  });
  // Filter can return one row per catalog for the supplier; pick the selected catalog's row.
  const supplierInfo = useMemo(() => {
    const items = supplierInfoData?.items;
    if (!items?.length) {
      return undefined;
    }

    return (
      items.find((item) => item.catalogId === selectedCatalog?.catalogId) ?? items[0]
    );
  }, [selectedCatalog?.catalogId, supplierInfoData?.items]);

  const {
    data: programInfo,
    isLoading: isLoadingProgramInfo,
    isError: isErrorProgramInfo,
    isFetched: isProgramInfoFetched,
  } = useGetProgramInfo(selectedProgramInfoId);

  const isSupplierInfoPanelOpen =
    selectedCatalog !== null && !isLoadingSupplierInfo && !!supplierInfo;

  const isGridLoading =
    isLoadingCatalogs || isLoadingSupplierInfo || isLoadingProgramInfo;

  useEffect(() => {
    if (selectedCatalog === null || !isErrorSupplierInfo) {
      return;
    }

    toast.error(t('Failed to load data'));
    setSelectedCatalog(null);
  }, [isErrorSupplierInfo, selectedCatalog, t]);

  useEffect(() => {
    if (selectedProgramInfoId === null || isLoadingProgramInfo) {
      return;
    }

    if (isErrorProgramInfo) {
      toast.error(
        t('Unable to open program information. Please connect with the administrator.')
      );
      setSelectedProgramInfoId(null);
      return;
    }

    if (!isProgramInfoFetched || !programInfo) {
      return;
    }

    if (programInfo.myAvendraLink) {
      redirect(programInfo.myAvendraLink, true, true);
    } else {
      toast.error(
        t('Unable to open program information. Please connect with the administrator.')
      );
    }

    setSelectedProgramInfoId(null);
  }, [
    isErrorProgramInfo,
    isLoadingProgramInfo,
    isProgramInfoFetched,
    programInfo,
    redirect,
    selectedProgramInfoId,
    t,
  ]);

  const openSupplierInfo = useCallback(
    (catalog: SupplierCatalog) => {
      if (catalog.supplierId == null) {
        toast.error(t('Failed to load data'));
        return;
      }

      setSelectedCatalog(catalog);
    },
    [t]
  );

  const openProgramInfo = useCallback((catalogProgramId: string) => {
    if (!catalogProgramId) {
      return;
    }

    setSelectedProgramInfoId(catalogProgramId);
  }, []);

  const closeSupplierInfo = useCallback(() => {
    setSelectedCatalog(null);
  }, []);

  const categoryOptions = useMemo(
    () => getCategoryOptionsForSuppliers(catalogs, marketplaceCategories, selectedSupplier),
    [catalogs, marketplaceCategories, selectedSupplier]
  );

  const resolvedSelectedCategory = useMemo(
    () => resolveSelectedCategory(selectedCategory, categoryOptions, t('All Categories')),
    [categoryOptions, selectedCategory, t]
  );

  const catalogDataState = useMemo<State>(
    () => ({
      ...dataState,
      filter: buildBrowseCatalogsFilter(
        selectedSupplier,
        resolvedSelectedCategory,
        debouncedSearchText
      ),
    }),
    [dataState, resolvedSelectedCategory, debouncedSearchText, selectedSupplier]
  );

  const hasActiveFilters = useMemo(() => {
    const defaultSort = DEFAULT_BROWSE_CATALOGS_DATA_STATE.sort?.[0];
    const currentSort = dataState.sort?.[0];
    const hasGridFilters = (catalogDataState.filter?.filters?.length ?? 0) > 0;

    return (
      hasGridFilters ||
      searchText.trim().length > 0 ||
      currentSort?.field !== defaultSort?.field ||
      currentSort?.dir !== defaultSort?.dir
    );
  }, [catalogDataState.filter?.filters?.length, dataState.sort, searchText]);


  const clearFilters = useCallback(() => {
    setSearchText('');
    setSelectedSupplier(SUPPLIER_TYPE_FILTER_ITEMS);
    setSelectedCategory(DEFAULT_SELECTED_CATEGORY);
    setDataState(DEFAULT_BROWSE_CATALOGS_DATA_STATE);
  }, []);

  const handleSupplierChange = useCallback((supplier: SupplierOption[]) => {
    setSelectedSupplier(
      supplier.map((item) => {
        const defaultItem = SUPPLIER_TYPE_FILTER_ITEMS.find((option) => option.field === item.field);
        return defaultItem ? { ...defaultItem, show: item.show, display: item.display } : item;
      })
    );
  }, []);

  const value = useMemo<ViewCatalogsContextShape>(
    () => ({
      catalogs,
      catalogDataState,
      isLoadingCatalogs,
      isGridLoading,
      categoryOptions,
      selectedCategory: resolvedSelectedCategory,
      setSelectedCategory,
      selectedSupplier,
      setSelectedSupplier: handleSupplierChange,
      searchText,
      setSearchText,
      dataState,
      setDataState,
      hasActiveFilters,
      clearFilters,
      selectedCatalog,
      supplierInfo,
      isLoadingSupplierInfo,
      isSupplierInfoPanelOpen,
      openSupplierInfo,
      closeSupplierInfo,
      openProgramInfo,
    }),
    [
      catalogDataState,
      catalogs,
      categoryOptions,
      clearFilters,
      closeSupplierInfo,
      dataState,
      handleSupplierChange,
      hasActiveFilters,
      isLoadingCatalogs,
      isGridLoading,
      isLoadingSupplierInfo,
      isSupplierInfoPanelOpen,
      openProgramInfo,
      openSupplierInfo,
      resolvedSelectedCategory,
      searchText,
      selectedCatalog,
      selectedSupplier,
      supplierInfo,
    ]
  );

  return (
    <ViewCatalogsContext.Provider value={value}>{children}</ViewCatalogsContext.Provider>
  );
};
